import { useCallback, useEffect } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import { byServerPosition } from "@/lib/tableOrder";
import { bootstrapOfflineDb, cacheBootstrapFromServer, cacheDisplaySettings, dbStore, defaultPrinters, defaultSettings, offlineDb, putSetting, queueMutation, replaceCollection, saveSyncMeta } from "@/db/offlineDb";

const resolveUpdate = (updater, current) => typeof updater === "function" ? updater(current) : updater;

const EMPTY = [];

export function useLocalCollections(token, terminalId) {
  // useLiveQuery returns `undefined` until its first query resolves, and the third
  // argument replaces that with a default — which is exactly what made "still
  // reading IndexedDB" indistinguishable from "this terminal has no menu". Every
  // screen then had to choose a single message for both, and chose the wrong one:
  // the floor said "not synced" a frame after mount, the till said "try a different
  // search term" for a menu that had never loaded. Drop the default on the two
  // collections whose emptiness is load-bearing, keep `[]` for consumers by
  // coalescing here, and publish the resolution as its own flag.
  // Menu and areas are sorted back into the server's arrangement: IndexedDB
  // returns rows by key (string ids, area names), not in the order they were sent.
  const menuRows = useLiveQuery(async () => (await offlineDb.menu.toArray()).sort(byServerPosition), []);
  const areaRows = useLiveQuery(async () => (await offlineDb.areas.toArray()).sort(byServerPosition), []);
  const menu = menuRows ?? EMPTY;
  const areas = areaRows ?? EMPTY;
  const menuHydrated = menuRows !== undefined;
  const areasHydrated = areaRows !== undefined;
  const areaTables = useLiveQuery(() => offlineDb.area_tables.toArray(), [], []);
  const tables = useLiveQuery(() => dbStore('tables').toArray(), [], []);
  const tickets = useLiveQuery(() => offlineDb.tickets.toArray(), [], []);
  const printers = useLiveQuery(() => offlineDb.printers.toArray(), [], defaultPrinters);
  const paymentModes = useLiveQuery(() => offlineDb.payment_modes.toArray(), [], []);
  const orderTypes = useLiveQuery(() => offlineDb.order_types.toArray(), [], []);
  const settings = useLiveQuery(async () => (await offlineDb.settings.get("main"))?.value || defaultSettings, [], defaultSettings);

  useEffect(() => {
    import("@/lib/format").then(mod => mod.updateFormatContext(settings));
  }, [settings]);

  useEffect(() => {
    sessionStorage.setItem("vibez-terminal-id", terminalId);
    saveSyncMeta(terminalId);
    bootstrapOfflineDb().then(() => cacheBootstrapFromServer(token));
  }, [terminalId, token]);

  // Re-read the SETTINGS document on demand -- and nothing else.
  //
  // This first called cacheBootstrapFromServer, which was wrong and broke the
  // floor. That function clears and re-puts `tables` and `area_tables` (and the
  // menu) from the server snapshot. Wiring it to the settings_updated broadcast
  // meant every terminal wiped its cached floor every time anyone saved a
  // setting, so captains lost their local occupancy overlay and each snapped to
  // whatever the server said at that instant -- three devices, three different
  // views of the same table.
  //
  // cacheDisplaySettings is the one path allowed to mirror server settings onto
  // a terminal (its own comment says so). It touches the settings document only.
  // The floor changed on some terminal (tables added, moved, resized; areas
  // reordered): re-read the board data — tables, areas, spans — into the cache.
  const refreshFloor = useCallback(() => {
    if (!token) return Promise.resolve();
    return cacheBootstrapFromServer(token).catch(() => {});
  }, [token]);

  const refreshSettings = useCallback(() => {
    if (!token) return Promise.resolve();
    return cacheDisplaySettings(token);
  }, [token]);

  const persistCollection = useCallback((name, current, type, updater) => {
    const next = resolveUpdate(updater, current);
    replaceCollection(name, next);
    queueMutation(type, name, { rows: next }, terminalId);
    return next;
  }, [terminalId]);

  const setMenu = useCallback(updater => persistCollection("menu", menu, "UPDATE_MENU", updater), [menu, persistCollection]);
  const setTables = useCallback(updater => persistCollection("tables", tables, "UPDATE_TABLE_STATUS", updater), [persistCollection, tables]);
  const setAreaTables = useCallback(updater => persistCollection("area_tables", areaTables, "UPDATE_AREA_TABLE_STATUS", updater), [areaTables, persistCollection]);
  const setTickets = useCallback(updater => persistCollection("tickets", tickets, "UPDATE_KOT", updater), [persistCollection, tickets]);
  const setPrinters = useCallback(updater => persistCollection("printers", printers, "UPDATE_PRINTERS", updater), [persistCollection, printers]);
  // The settings document is a TERMINAL cache and nothing more. It used to queue an
  // UPDATE_SETTINGS mutation as well, which was never anything but a no-op:
  // sync_service._handle_update_settings looks each camelCase key up in
  // restaurant_settings (`restaurantName`, `gst`, `receiptFooter`…), finds no row,
  // `continue`s, and commits the action ACCEPTED with "0 setting(s) updated" — so
  // the queue reported success for a write that never happened. Server settings are
  // written by Admin through PUT /api/settings/{key} against the real key names
  // (lib/settingsMap.js); no other path may pretend to.
  const setSettings = useCallback(updater => {
    const next = resolveUpdate(updater, settings);
    putSetting(next);
    return next;
  }, [settings]);

  return { refreshSettings, refreshFloor, menu, menuHydrated, setMenu, areas, areasHydrated, areaTables, setAreaTables, tables, setTables, tickets, setTickets, printers, setPrinters, paymentModes, orderTypes, settings, setSettings };
}